<template>
  <div class="owner-history">
    <div v-if="isLoading" class="pa-4 text-center grey--text">Loading owner history&hellip;</div>
    <div v-else-if="!yearGroups.length" class="pa-4 grey--text">
      No tax records loaded for this property.
    </div>
    <div v-else class="owner-history-scroll">
      <v-simple-table dense class="owner-history-table">
        <thead>
          <tr>
            <th class="text-left" style="width: 70px;">Year</th>
            <th class="text-left" style="width: 90px;">Tax ID</th>
            <th class="text-left" style="width: 150px;">Changed On</th>
            <th class="text-left" style="width: 130px;">By</th>
            <th class="text-left" style="width: 260px;">Fields Changed</th>
            <!-- Equal widths: the Before/After cell below is a single colspan=2 whose two
                 halves split 50/50, so these have to match or the headers sit off-column. -->
            <th class="text-left" style="width: 23%;">Before</th>
            <th class="text-left" style="width: 23%;">After</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="group in yearGroups">
            <tr :key="`${group.key}-head`" class="owner-history-year-row">
              <td colspan="7">
                <strong>{{ group.taxYear }}</strong>
                &nbsp;&middot;&nbsp;Tax ID {{ group.taxId }}
                <span class="grey--text">
                  &nbsp;&middot;&nbsp;{{ groupCaption(group) }}
                </span>
              </td>
            </tr>
            <tr v-for="row in group.rows" :key="row._id">
              <td>{{ row.taxYear }}</td>
              <td>{{ row.taxId }}</td>
              <td>{{ formatChangedOn(row.changedOn) }}</td>
              <td>
                {{ row.userName }}
                <div class="grey--text caption">{{ formatWkst(row.wkstNumber) }}</div>
              </td>
              <td>
                <v-chip
                  v-for="(field, idx) in row.fields"
                  :key="`${row._id}-f-${idx}`"
                  x-small label outlined class="mr-1 mb-1"
                >{{ field }}</v-chip>
                <span v-if="!row.fields.length" class="grey--text">&mdash;</span>
                <div v-if="row.note" class="owner-history-note">{{ row.note }}</div>
              </td>
              <!-- Before and After share ONE cell, laid out as a row per address line.
                   Two independent columns drift apart the moment a line wraps on one side
                   only ('2901 S 115TH EAST AVE APT D' takes two lines, '5507 N JOHNSTOWN
                   AVE' takes one), which pushes every line under it out of step with its
                   pair. A flex row per line makes both halves share a height instead. -->
              <td colspan="2" class="owner-history-diff">
                <div
                  v-for="(line, idx) in row.lines"
                  :key="`${row._id}-l-${idx}`"
                  class="owner-history-line"
                >
                  <div class="owner-history-side" :class="{ 'owner-history-removed': line.changed }">{{ line.from }}</div>
                  <div class="owner-history-side" :class="{ 'owner-history-added': line.changed }">{{ line.to }}</div>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </v-simple-table>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { groupOwnerHistoryByYear } from '../TaxInquiryUtils/ownerHistory.utils';
import { toLocalDateTime } from '../../../../util/shared/vue-global';

// Matches the backend's WKST_UNKNOWN, shown when a comment has no workstation on it.
const WKST_UNKNOWN = 'n/a';

export default {
  name: 'OwnerHistoryList',
  data() {
    return {
      comments: [],
      isLoading: false,
      // When the county's owner change history begins. A converted county has nothing
      // structured before it, so a year with no rows says so rather than reading as
      // "this address was never touched".
      historyStartDate: null,
    };
  },
  props: {
    // The (taxYear, taxId) pairs the History tab loaded for this property - every year of
    // the record. Owner comments are fetched for exactly these pairs; see the backend
    // commentFilter for why taxId alone will not do.
    taxRecords: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    yearGroups() {
      return groupOwnerHistoryByYear(this.taxRecords, this.comments);
    },
  },
  watch: {
    taxRecords: {
      immediate: true,
      handler() {
        this.reload();
      },
    },
  },
  created() {
    this.loadOwnerChangeHistoryStartDate().then((date) => {
      this.historyStartDate = date;
    });
  },
  methods: {
    ...mapActions('tax/taxComments', ['loadOwnerChangeHistory', 'loadOwnerChangeHistoryStartDate']),
    async reload() {
      const { taxRecords } = this;
      if (!(taxRecords || []).length) {
        this.comments = [];
        return;
      }
      this.isLoading = true;
      try {
        this.comments = await this.loadOwnerChangeHistory({ taxRecords });
      } finally {
        this.isLoading = false;
      }
    },
    groupCaption(group) {
      const count = group.rows.length;
      if (count) return count === 1 ? '1 change' : `${count} changes`;
      if (this.historyStartDate) {
        return `no owner change history before ${this.formatStartDate(this.historyStartDate)}`;
      }
      return 'no owner changes recorded';
    },
    // The stored value is a UTC instant; it reads in Central (the system standard), not in
    // whatever zone this workstation's browser is set to.
    formatChangedOn(value) {
      return toLocalDateTime(value);
    },
    formatStartDate(value) {
      if (!value) return '';
      // A date-only 'YYYY-MM-DD' parses as UTC midnight, which is the previous day in any
      // US zone - read the parts out rather than letting the local zone shift it.
      const [year, month, day] = String(value).slice(0, 10).split('-');
      if (!year || !month || !day) return '';
      return `${month}/${day}/${year}`;
    },
    formatWkst(wkstNumber) {
      // Tulsa runs with useWkstNoFromUser off, so a session started without the
      // workstationID parameter genuinely has no workstation to show.
      return `Wkst-${wkstNumber === null || wkstNumber === undefined || wkstNumber === '' ? WKST_UNKNOWN : wkstNumber}`;
    },
  },
};
</script>

<style scoped>
/* An owner row is four-ish address lines tall, so a handful fill far more space than the
   yearly grid's rows do. Sized off the viewport rather than a fixed pixel height: at 260px
   only two changes fit and the years below them were unreachable. */
.owner-history-scroll {
  max-height: calc(100vh - 330px);
  min-height: 220px;
  overflow-y: auto;
}
.owner-history-table >>> td {
  vertical-align: top;
  padding-top: 6px;
  padding-bottom: 6px;
  font-size: 12px;
}
.owner-history-year-row td {
  background-color: #eceff1;
  font-size: 12px;
}
.owner-history-line {
  display: flex;
  align-items: stretch;
}
.owner-history-side {
  flex: 1 1 50%;
  min-width: 0;
  white-space: pre-wrap;
  line-height: 1.35;
  /* Keeps a padded-out line (one side has an address line the other does not) at full
     height so the two blocks stay in step. */
  min-height: 1.35em;
  padding-right: 8px;
}
/* Old value on the left, new value on the right - the same yellow/green the mockup uses. */
.owner-history-removed {
  background-color: #fff3c4;
}
.owner-history-added {
  background-color: #d7f2d9;
}
.owner-history-note {
  color: rgba(0, 0, 0, 0.6);
  font-style: italic;
  white-space: pre-wrap;
}
</style>
